import { useEffect, useRef } from 'react';
import {
	WarningCircle as AlertCircle,
	CheckCircle,
	Timer,
	CreditCard,
	LockSimple as Lock,
	X as Close
} from '@phosphor-icons/react';

export type ConfirmState =
	| 'idle'
	| 'charging'
	| 'success'
	| 'error'
	| 'expired';

/**
 * Confirmação da compra one-click: um clique na oferta NÃO cobra — abre este
 * resumo (produto, valor, parcela, cartão) e só o "Confirmar pagamento"
 * dispara a cobrança. Enquanto cobra, não fecha (nem Esc nem clique fora).
 */
export const ConfirmDialog = ({
	open,
	state,
	errorMessage,
	productTitle,
	productImage,
	amountLabel,
	cardLabel,
	countdown,
	accentStyle,
	isPreview,
	onConfirm,
	onClose,
	onSkip
}: {
	open: boolean;
	state: ConfirmState;
	errorMessage?: string;
	productTitle: string;
	productImage: string | null;
	amountLabel: string | null;
	cardLabel: string;
	countdown: string | null;
	/** Fundo e texto do botão (ver checkoutButtonColors). */
	accentStyle?: { backgroundColor?: string; color?: string };
	isPreview: boolean;
	onConfirm: () => void;
	onClose: () => void;
	onSkip: () => void;
}) => {
	const confirmRef = useRef<HTMLButtonElement>(null);
	const isCharging = state === 'charging';
	const locked = isCharging || state === 'success';

	// Foco no botão principal ao abrir; Esc fecha (exceto cobrando); a página
	// por trás não rola com o diálogo aberto.
	useEffect(() => {
		if (!open) return;
		confirmRef.current?.focus();
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		const onKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape' && !locked) onClose();
		};
		window.addEventListener('keydown', onKey);
		return () => {
			document.body.style.overflow = previousOverflow;
			window.removeEventListener('keydown', onKey);
		};
	}, [open, locked, onClose]);

	if (!open) return null;

	return (
		<div
			className="upsell-dialog-backdrop"
			onMouseDown={(event) => {
				if (event.target === event.currentTarget && !locked) onClose();
			}}
		>
			<div
				className="upsell-dialog"
				role="dialog"
				aria-modal="true"
				aria-labelledby="upsell-dialog-title"
			>
				{state === 'success' ? (
					<div
						className="upsell-dialog-result is-success"
						role="status"
					>
						<CheckCircle size={40} />
						<strong>Pagamento aprovado!</strong>
						<span>
							{productTitle} foi adicionado ao seu pedido. Você
							recebe o comprovante por e-mail.
						</span>
					</div>
				) : state === 'expired' ? (
					<div className="upsell-dialog-result" role="alert">
						<Timer size={36} />
						<strong>O tempo desta oferta acabou</strong>
						<span>Nada foi cobrado no seu cartão.</span>
						<button
							type="button"
							className="upsell-dialog-confirm"
							style={accentStyle}
							onClick={onSkip}
						>
							Continuar
						</button>
					</div>
				) : (
					<>
						<div className="upsell-dialog-header">
							<h2 id="upsell-dialog-title">
								Confirme sua compra
							</h2>
							<button
								type="button"
								className="upsell-dialog-close"
								aria-label="Fechar"
								disabled={locked}
								onClick={onClose}
							>
								<Close size={18} />
							</button>
						</div>

						<div className="upsell-dialog-product">
							{productImage ? (
								<img src={productImage} alt="" />
							) : (
								<span className="is-empty" />
							)}
							<span>{productTitle}</span>
						</div>

						<dl className="upsell-dialog-summary">
							<div>
								<dt>Forma de pagamento</dt>
								<dd>
									<CreditCard size={14} /> {cardLabel}
								</dd>
							</div>
							<div>
								<dt>Parcelamento</dt>
								<dd>1x de {amountLabel ?? '—'} (à vista)</dd>
							</div>
							<div className="is-total">
								<dt>Total</dt>
								<dd>{amountLabel ?? '—'}</dd>
							</div>
						</dl>

						<p className="upsell-dialog-note">
							É uma cobrança nova e separada da compra que você
							acabou de fazer. Ao confirmar, você autoriza a
							cobrança de {amountLabel ?? 'valor acima'} no cartão{' '}
							{cardLabel}.
						</p>

						{state === 'error' && (
							<p className="upsell-dialog-error" role="alert">
								<AlertCircle size={16} />
								<span>
									{errorMessage} Nada foi cobrado — você pode
									tentar de novo.
								</span>
							</p>
						)}

						<button
							ref={confirmRef}
							type="button"
							className="upsell-dialog-confirm"
							style={accentStyle}
							disabled={isCharging || isPreview || !amountLabel}
							onClick={onConfirm}
						>
							{isCharging ? (
								<>
									<span
										className="upsell-spinner"
										aria-hidden
									/>
									Processando pagamento…
								</>
							) : isPreview ? (
								'Pré-visualização — nada é cobrado'
							) : state === 'error' ? (
								`Tentar novamente · ${amountLabel}`
							) : (
								`Confirmar pagamento · ${amountLabel}`
							)}
						</button>
						<button
							type="button"
							className="upsell-dialog-back"
							disabled={isCharging}
							onClick={state === 'error' ? onSkip : onClose}
						>
							{state === 'error'
								? 'Seguir sem a oferta'
								: 'Voltar'}
						</button>

						<p className="upsell-dialog-secure">
							<Lock size={12} /> Pagamento seguro
							{countdown && (
								<>
									<span className="upsell-dot">·</span>
									<Timer size={12} /> oferta válida por{' '}
									<strong>{countdown}</strong>
								</>
							)}
						</p>
					</>
				)}
			</div>
		</div>
	);
};
